import { Sparkles, Target, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RadarChart } from '@/components/radar-chart'
import { ScaleChip, ScaleLegend, scaleLabelFor } from '@/components/scale-legend'
import {
  focusAreas,
  isAiQuestion,
  strengths,
  type AreaResult,
  type SelfAssessmentCatalog,
} from '@/lib/self-assessment'

/**
 * One finished self-assessment, read back: the shape (radar), what to work on
 * first, what is strong, and every area's number in a table — the radar is the
 * summary, the table is the evidence (the same rule radar-chart.tsx states).
 *
 * Shared by the person's own page and the coach's view of that person, so the
 * coach is looking at exactly what the person saw.
 */
export function SelfAssessmentResults({
  catalog,
  results,
  previous,
  nowLabel = 'This time',
  previousLabel = 'Last time',
}: {
  catalog: SelfAssessmentCatalog
  results: AreaResult[]
  /** The person's previous finished run, when there is one. */
  previous?: AreaResult[] | null
  nowLabel?: string
  previousLabel?: string
}) {
  const { template } = catalog
  const focus = focusAreas(results)
  const strong = strengths(results)
  const ai = results.flatMap((r) => r.questions).filter((q) => isAiQuestion(q.criterion) && q.score != null)
  const aiAverage = ai.length ? ai.reduce((s, q) => s + (q.score as number), 0) / ai.length : null
  const beforeByCat = new Map((previous ?? []).map((r) => [r.category.id, r.average]))

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardContent className="pt-5">
            <RadarChart
              axes={results.map((r) => ({
                label: r.category.name,
                now: r.average,
                before: previous ? beforeByCat.get(r.category.id) ?? null : undefined,
              }))}
              template={template}
              nowLabel={nowLabel}
              beforeLabel={previous ? previousLabel : null}
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Target className="h-4 w-4 text-primary" /> Work on these first
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {focus.map((r) => (
                <div key={r.category.id}>
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    {r.category.name}
                    {r.average != null && <ScaleChip template={template} score={round1(r.average)} />}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {r.questions
                      .filter((q) => q.score != null && q.score <= 3)
                      .slice(0, 2)
                      .map((q) => (
                        <li key={q.criterion.id}>
                          <span className="tabular-nums">{q.score}</span> · {q.criterion.name}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-primary" /> Strengths
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {strong.map((r) => (
                  <div key={r.category.id} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">{r.category.name}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {round1(r.average as number)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-primary" /> Working with AI
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {aiAverage != null ? (
                  <>
                    <span className="text-lg font-semibold tabular-nums">{round1(aiAverage)}</span>{' '}
                    <span className="text-muted-foreground">{scaleLabelFor(template, aiAverage)}</span>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Across the {ai.length} AI questions, spread through the areas.
                    </p>
                  </>
                ) : (
                  <span className="text-muted-foreground">Not answered yet.</span>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ScaleLegend template={template} />

      <Card>
        <CardContent className="divide-y p-0">
          {results.map((r) => {
            const before = beforeByCat.get(r.category.id)
            return (
              <div key={r.category.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 font-medium">{r.category.name}</span>
                {previous && before != null && r.average != null && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    was {round1(before)}
                  </span>
                )}
                {r.average != null ? (
                  <ScaleChip template={template} score={round1(r.average)} />
                ) : (
                  <span className="text-xs text-muted-foreground">not answered</span>
                )}
              </div>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10
}
